import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingCard } from "@/components/LoadingCard";
import { Button } from "@/components/ui/button";
import { Crown, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { AuthGate } from "@/components/AuthGate";
import { useHasFeature } from "@/lib/useHasFeature";
import { useHomeRoute } from "@/hooks/use-home-route";
import Layout from "@/components/Layout";
import { useTranslation } from "react-i18next";
import { useMyDataQuery } from "@/generated/graphql";
import { useOpenPlans } from "@/hooks/use-open-plans";
import { currentEmbeddedApp } from "@/lib/app-embed/readEmbeddedApp";

interface ProOnlyRouteProps {
  children: React.ReactNode;
  /** Admins pass without PRO (FreeCut Studio, open to every admin). */
  allowAdmins?: boolean;
}

const ProOnly = ({ children, allowAdmins = false }: ProOnlyRouteProps) => {
  const { loading: myDataLoading, data: myData } = useMyDataQuery();
  const hasProFeatures = useHasFeature('pro_features');
  const { route: homeRoute } = useHomeRoute();
  const { t } = useTranslation();
  const openPlans = useOpenPlans();

  if (myDataLoading) {
    return <LoadingCard fullScreen message={t('common.loading')} />;
  }

  if (!hasProFeatures && !(allowAdmins && myData?.myData?.is_admin === true)) {
    return (
      <Layout>
        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-lg text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-primary">
                <Crown className="w-8 h-8 text-black" />
              </div>
              <CardTitle className="text-2xl">{t('proOnlyRoute.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Lock className="w-5 h-5" />
                <span>{t('proOnlyRoute.locked')}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('proOnlyRoute.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button asChild variant="default" className="gap-2">
                  <Link
                    to="/plans"
                    onClick={(event) => {
                      if (!currentEmbeddedApp()) return;
                      event.preventDefault();
                      openPlans();
                    }}
                  >
                    <Crown className="w-4 h-4" />
                    {t('proOnlyRoute.upgradeButton')}
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={homeRoute}>
                    {t('proOnlyRoute.backToProjects')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return <>{children}</>;
};

const ProOnlyRoute = ({ children, allowAdmins }: ProOnlyRouteProps) => {
  return (
    <AuthGate>
      <ProOnly allowAdmins={allowAdmins}>{children}</ProOnly>
    </AuthGate>
  );
};

export default ProOnlyRoute;
